import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useApi } from './ApiContext';
import { useAuth } from './AuthContext';

interface UserChatContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  toggleChat: () => void;
  unreadCount: number;
  unreadMentions: number;
  refreshUnreadStatus: () => Promise<void>;
}

const UserChatContext = createContext<UserChatContextType | undefined>(undefined);

export const UserChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMentions, setUnreadMentions] = useState(0);
  const { api } = useApi();
  const { isAuthenticated } = useAuth();

  const refreshUnreadStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setUnreadMentions(0);
      return;
    }

    try {
      const response = await api.get('/chat/unread');
      setUnreadCount(response.data.unreadCount);
      setUnreadMentions(response.data.unreadMentions);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        // Sesión no válida aún (p. ej. antes de login). No loguear como error.
        setUnreadCount(0);
        setUnreadMentions(0);
        return;
      }
      console.error('Error fetching unread chat status:', error);
    }
  }, [api, isAuthenticated]);

  // Polling para notificaciones con optimizaciones
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Verificar inmediatamente al montar
    refreshUnreadStatus();
    
    // Usar Page Visibility API para detener polling cuando la pestaña no está activa
    let interval: ReturnType<typeof setInterval> | null = null;
    
    const startPolling = () => {
      if (interval) clearInterval(interval);
      // Polling cada 30 segundos (reducido de 15s para menos carga)
      interval = setInterval(refreshUnreadStatus, 30000);
    };
    
    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
    
    // Iniciar polling solo si la pestaña está visible
    if (document.visibilityState === 'visible') {
      startPolling();
    }
    
    // Manejar cambios de visibilidad de la pestaña
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Cuando la pestaña se vuelve visible, verificar inmediatamente y luego iniciar polling
        refreshUnreadStatus();
        startPolling();
      } else {
        // Cuando la pestaña se oculta, detener polling para ahorrar recursos
        stopPolling();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, refreshUnreadStatus]);

  // Cuando se abre el chat, marcar como leído
  useEffect(() => {
    if (isOpen && isAuthenticated) {
      api.post('/chat/read')
        .then(() => {
          setUnreadCount(0);
          setUnreadMentions(0);
        })
        .catch(err => console.error('Error marking chat read:', err));
    }
  }, [isOpen, isAuthenticated, api]);

  const toggleChat = () => setIsOpen(prev => !prev);

  return (
    <UserChatContext.Provider value={{
      isOpen,
      setIsOpen,
      toggleChat,
      unreadCount,
      unreadMentions,
      refreshUnreadStatus
    }}>
      {children}
    </UserChatContext.Provider>
  );
};

export const useUserChat = () => {
  const context = useContext(UserChatContext);
  if (context === undefined) {
    throw new Error('useUserChat must be used within a UserChatProvider');
  }
  return context;
};

